import type { LobbyServerEvent } from '@danda/shared';
import type { EventHandlerContext } from './lobby-event-types';
import { msgId } from './stream-utils';

/** Room id for the account-level friend DM with a given user */
export function friendDmRoomId(partnerUserId: string): string {
  return `friend-dm-${partnerUserId}`;
}

/** Friends-system events: friends list, friend-added, account-level DMs */
export function handleFriendEvents(event: LobbyServerEvent, ctx: EventHandlerContext): boolean {
  const { get } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:friends-list-result': {
      state.setFriends(event.friends);
      return true;
    }

    case 'lobby:friend-added': {
      // The new friend was just active in a chat with us, so mark them online;
      // the next friends-list refresh corrects presence either way. The
      // character fields aren't on this event, so the row stays out of the
      // /invite picker until that refresh fills them in.
      state.upsertFriend({
        userId: event.friendUserId,
        displayName: event.friendDisplayName,
        online: true,
        inSession: false,
        inParty: false,
      });
      if (!event.alreadyFriends) {
        state.addNotification({
          id: `notif-${Date.now()}`,
          type: 'system',
          title: 'New Friend',
          message: `You are now friends with ${event.friendDisplayName}`,
          timestamp: new Date(event.sent_at ?? Date.now()),
          isRead: false,
          data: { friendUserId: event.friendUserId },
        });
      }
      return true;
    }

    case 'lobby:friend-removed': {
      // Removal is mutual, so this arrives whichever side initiated it. Close
      // the DM room too: the server already tore the session down.
      state.removeFriend(event.friendUserId);
      const roomId = friendDmRoomId(event.friendUserId);
      if (state.chatRooms.some((r) => r.id === roomId)) {
        state.removeChatRoom(roomId);
        if (state.activeChatRoomId === roomId) {
          state.switchChatRoom('main');
        }
      }
      return true;
    }

    case 'lobby:friend-dm-started': {
      const roomId = friendDmRoomId(event.partnerUserId);
      const existing = state.chatRooms.find((r) => r.id === roomId);
      if (!existing) {
        state.addChatRoom({
          id: roomId,
          name: event.partnerDisplayName,
          participants: [event.partnerUserId],
          messages: [],
          unreadCount: 0,
          type: 'private',
        });
      }
      state.switchChatRoom(roomId);
      return true;
    }

    case 'lobby:friend-dm-message': {
      const roomId = friendDmRoomId(event.fromUserId);
      const room = state.chatRooms.find((r) => r.id === roomId);
      if (!room) {
        // First message can arrive before (or without) a local started event;
        // auto-open the DM room.
        state.addChatRoom({
          id: roomId,
          name: event.fromDisplayName,
          participants: [event.fromUserId],
          messages: [],
          unreadCount: 0,
          type: 'private',
        });
      }
      state.addMessage(roomId, {
        id: msgId('friend-dm'),
        senderId: event.fromUserId,
        senderName: event.fromDisplayName,
        content: event.message,
        timestamp: new Date(event.sent_at),
        type: 'user',
      });
      return true;
    }

    case 'lobby:friend-dm-closed': {
      const roomId = friendDmRoomId(event.partnerUserId);
      state.removeChatRoom(roomId);
      if (state.activeChatRoomId === roomId) {
        state.switchChatRoom('main');
      }
      return true;
    }

    default:
      return false;
  }
}
